import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { canUseStac } from 'src/app/services/stac-session.service';
import { StacConversionService, StacPressNotice, StacTaskView } from 'src/app/services/stac-conversion.service';

/** How a line of the banner reads: plain text, a muted hint, or a state with its icon. */
export type StacBannerTone = 'plain' | 'hint' | 'working' | 'success' | 'warning' | 'error';

export interface StacBannerLine {
  text: string;
  tone: StacBannerTone;
}

/**
 * Scene errors a sync can't fix, so they don't bring the button back (D6): not publishable (shown
 * as "Not available in Data Catalog", like a skipped scene) and an ambiguous task (contact
 * support). An archived source (`source_archived`) waits for its sensor's restore (`restoreFirst`),
 * after which a sync fixes it (`needsSync`). Every other error's own hint says to sync again.
 */
const NOT_FIXED_BY_SYNC: ReadonlySet<string> = new Set(['not_publishable', 'source_archived', 'task_ambiguous']);

/** A sensor whose files are in Glacier: a request skips it until it's restored. */
const ARCHIVED = 'archived';

const TONE_ICONS: Readonly<Record<StacBannerTone, string | null>> = Object.freeze({
  plain: null,
  hint: null,
  working: 'autorenew',
  success: 'check_circle',
  warning: 'warning',
  error: 'error',
});

/**
 * The Data Catalog banner for the task shown on My Collections (plans/stac-conversion-eetaskapi.md
 * §8.2, decision 26; "Add to STAC" there): a full-width strip above the scene table, with the
 * "Sync Data Catalog" button and, in plain sight rather than a tooltip, everything about the task's
 * sync: what is still waiting, and what a press does (not the latest request's own status, nor
 * counts: the scenes say how it went). While a request is queued or running, a progress bar along
 * the strip's bottom edge counts the task's delivered scenes through. One per page. Once some of
 * the task is in the catalog a press resyncs the task (§6.2); the hint is the same either way. A
 * press shows nothing else: the state it reads afterwards is the answer, and a refusal that state can't show
 * is the banner's first line.
 *
 * It reads the task's state (StacConversionService) when the task is shown, after a press, and
 * every 10 seconds while a request is queued or running; the scenes' statuses
 * (app-stac-scene-status) share what it reads. It shows nothing for a user without STAC, or
 * where the server says "Add to STAC" isn't there: switched off, a plan without it, or a task the
 * user can't see. `shown` tells the page which.
 *
 * The button shows only when there is something for a sync to fix (`needsSync`, decision D6 in
 * plans/stac-permission-visibility.md): a delivered scene of a switched-on sensor that failed, was
 * interrupted, or isn't in the catalog yet. It is hidden while a request runs, while scenes are
 * still being added, and once every scene is in the catalog or can't be; the lines stay, so the
 * task's state is still in plain sight. The route refuses a second request with a 409, so a page
 * that's out of date gets that message.
 *
 * Once the task's processing is done and nothing is left to say (`complete`), the whole banner
 * hides, not only the button: every delivered scene in the catalog or not available, no failure,
 * nothing running or being added. A new delivery or a failure brings it back.
 */
@Component({
  selector: 'app-stac-conversion',
  templateUrl: './stac-conversion.component.html',
  styleUrls: ['./stac-conversion.component.scss'],
})
export class StacConversionComponent implements OnInit, OnChanges, OnDestroy {
  /** How often the status is read while a request is queued or running. */
  static readonly POLL_MS = 10000;

  @Input() taskId!: string;
  /**
   * The task's sensors as the collections page holds them, with their scenes. The banner shows only
   * once one of them has a delivered scene (StacConversionService.deliveredSensors): the page's own
   * rule for the download button, read afresh whenever the page reloads the task. The delivered
   * scenes are counted per sensor for the progress bar.
   */
  @Input() set sensors(sensors: any[] | null | undefined) {
    this.taskSensors = Array.isArray(sensors) ? sensors : [];
    this.delivered = StacConversionService.deliveredSensors(sensors);
    this.deliveredCounts = StacConversionComponent.deliveredSceneCounts(sensors);
  }

  /**
   * Whether it shows anything: true once the server says "Add to STAC" is there for the task;
   * false once it says not, for a user without STAC, and when this goes. A task still being read
   * changes nothing, so moving between tasks doesn't shift the page. Asynchronous, so that the
   * page's own bindings change in a turn of their own.
   */
  @Output() readonly shown = new EventEmitter<boolean>(true);

  view: StacTaskView;
  pressing = false;
  /** Why the latest press didn't queue a request, where the state doesn't say; cleared by the next press. */
  notice: StacPressNotice | null = null;

  readonly TITLE = 'Data Catalog';
  readonly LABEL = 'Sync Data Catalog';
  readonly ADD_HINT = 'Data are not yet in the Data Catalog. Click on the ‘Sync Data Catalog’ to add them.';
  readonly ACTIVE_HINT = 'A request is already in progress for this task';
  readonly RESTORE_HINT = 'This data has been archived. Request restoration first, and once completed, click ‘Sync Data Catalog.’';

  private eligible = false;
  /** The task's sensors as the page holds them, with their scenes, from `sensors`. */
  private taskSensors: any[] = [];
  /** The ids of the sensors with a delivered scene, from `sensors`. */
  private delivered: ReadonlySet<string> = new Set();
  /** How many delivered scenes each sensor has, by sensor id, from `sensors`. */
  private deliveredCounts: ReadonlyMap<string, number> = new Map();
  private shownTask: string | null = null;
  private announced = false;
  private userSub?: Subscription;
  private viewSub?: Subscription;
  private poll?: ReturnType<typeof setTimeout>;

  constructor(private stac: StacConversionService, private authService: AuthService, private cdr: ChangeDetectorRef) {}

  /** The delivered scenes of each sensor, counted as StacConversionService.deliveredSensors counts them. */
  static deliveredSceneCounts(sensors: any): Map<string, number> {
    const out = new Map<string, number>();
    for (const sensor of Array.isArray(sensors) ? sensors : []) {
      if (!sensor || typeof sensor.id !== 'string' || !sensor.id) continue;
      const scenes = Array.isArray(sensor.scenes) ? sensor.scenes : [];
      const n = scenes.filter((scene: any) => !!scene && !!scene.id && scene.status !== 'failed').length;
      if (n) out.set(sensor.id, n);
    }
    return out;
  }

  ngOnInit(): void {
    this.userSub = this.authService.user.subscribe((user) => {
      const eligible = canUseStac(user);
      if (eligible !== this.eligible || this.shownTask !== this.taskId) {
        this.eligible = eligible;
        this.show();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['taskId'] && !changes['taskId'].firstChange) this.show();
    // The same task, reloaded by the page: a delivery may have arrived. Another task's sensors
    // come with its id, above, and the state read for it says what shows.
    else if (changes['sensors'] && this.view) this.announce(this.visible);
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
    this.viewSub?.unsubscribe();
    this.stopPolling();
    this.announce(false);
  }

  get active(): boolean {
    return !!this.view && StacConversionService.isActive(this.view.request);
  }

  /**
   * Whether a press would do anything: Sync Data Catalog is switched on for at least one of the task's
   * sensors (decision 31), and that sensor has a delivered scene. A task of sensors that are all
   * off, or that STAC doesn't cover, shows no banner — a request that leaves every scene alone is
   * not worth offering; nor does a task still waiting for its first delivery, which the route
   * would refuse with nothing_delivered.
   */
  get covered(): boolean {
    return !!this.view && StacConversionService.anyDeliveredOn(this.view.sensors, this.delivered);
  }

  /**
   * Whether the task's processing is done with nothing left to say, so that the banner hides
   * entirely: every scene STAC must reach has an outcome (`stacProcessingDone`), each in the
   * catalog or not available, and nothing to sync. Not while a request is active or scenes are
   * being added, and not while a refusal of the latest press is shown. Read from the scenes as
   * they are, not from the latest request's status, `pending` or `archivedSensors`: those are
   * taken once, when it ends, and a delivery can finish what it left after it.
   */
  get complete(): boolean {
    const view = this.view;
    if (!view || !view.stacProcessingDone || this.active || this.pressing || this.notice || this.adding) return false;
    for (const entry of view.scenes.values()) if (entry.status !== 'success' && entry.status !== 'skipped') return false;
    return !this.needsSync;
  }

  /**
   * Whether scenes are being added to the catalog right now: an entry `converting` or `uploaded`
   * under a live claim (a delivery's own sync, or a request's). Read from the scenes as they are,
   * not from a finished request's `pending`: that count is taken once, when the request ends, and
   * a scene it counted can since have been interrupted, its claim run out, which a sync fixes.
   */
  get adding(): boolean {
    if (!this.view) return false;
    for (const entry of this.view.scenes.values()) if (entry.status === 'converting' || entry.status === 'uploaded') return true;
    return false;
  }

  /**
   * Whether the banner shows: a press would do something, the task isn't complete, and there is
   * something to say or press — no empty card while, say, a delivery is still adding its scenes.
   */
  get visible(): boolean {
    return this.covered && !this.complete && (this.needsSync || this.pressing || this.lines.length > 0);
  }

  /**
   * Whether the button shows (D6, plans/stac-permission-visibility.md): only when a sync has
   * something to fix, so that it is used when there is a problem.
   *
   * Hidden while a request is queued or running, and while any scene is being added right now
   * (`adding`, a delivery's own sync included). Otherwise shown when a switched-on sensor has a
   * scene that failed (an error a sync can fix, see NOT_FIXED_BY_SYNC), was interrupted, or is
   * delivered but has no entry yet — the page's own delivered scenes, as `deliveredSensors` counts
   * them, of a sensor that isn't archived. A scene whose source was archived counts once its
   * sensor isn't archived any more.
   * A scene that is in the catalog, or "Not available", needs nothing.
   */
  get needsSync(): boolean {
    const view = this.view;
    if (!view || this.active || this.adding) return false;
    const archived = this.archivedSensorIds;
    let fixable = false;
    for (const entry of view.scenes.values()) {
      if (view.sensors.get(entry.sensorUuid) !== 'on') continue;
      if (entry.status === 'interrupted') fixable = true;
      else if (entry.status === 'error' && !NOT_FIXED_BY_SYNC.has(entry.errorCode || '')) fixable = true;
      // An archived source whose sensor has since been restored: a sync reads it now.
      else if (entry.status === 'error' && entry.errorCode === 'source_archived' && !archived.has(entry.sensorUuid)) fixable = true;
    }
    if (fixable) return true;
    for (const sensor of this.taskSensors) {
      if (!sensor || typeof sensor.id !== 'string' || view.sensors.get(sensor.id) !== 'on' || sensor.status === ARCHIVED) continue;
      for (const scene of Array.isArray(sensor.scenes) ? sensor.scenes : []) {
        if (!scene || !scene.id || scene.status === 'failed') continue;
        const key = StacConversionService.sceneKey(sensor.id, scene);
        if (key && !view.scenes.has(key)) return true;
      }
    }
    return false;
  }

  /** The uuids of the task's sensors that are archived (in Glacier), as the page holds them. */
  private get archivedSensorIds(): Set<string> {
    const ids = new Set<string>();
    for (const sensor of this.taskSensors) if (sensor && typeof sensor.id === 'string' && sensor.status === ARCHIVED) ids.add(sensor.id);
    return ids;
  }

  /**
   * Whether the banner says to restore first (RESTORE_HINT): a switched-on sensor that is archived
   * (in Glacier) has a delivered scene that isn't in the catalog, or one a sync found archived
   * (`source_archived`), so only a restore can add it. Its scenes show nothing
   * (StacSceneStatusComponent): this is the one place that says so.
   */
  get restoreFirst(): boolean {
    const view = this.view;
    if (!view) return false;
    const archived = this.archivedSensorIds;
    for (const entry of view.scenes.values()) {
      if (entry.status !== 'error' || entry.errorCode !== 'source_archived') continue;
      if (archived.has(entry.sensorUuid) && view.sensors.get(entry.sensorUuid) === 'on') return true;
    }
    for (const sensor of this.taskSensors) {
      if (!sensor || typeof sensor.id !== 'string' || view.sensors.get(sensor.id) !== 'on' || sensor.status !== ARCHIVED) continue;
      for (const scene of Array.isArray(sensor.scenes) ? sensor.scenes : []) {
        if (!scene || !scene.id || scene.status === 'failed') continue;
        const key = StacConversionService.sceneKey(sensor.id, scene);
        if (key && !view.scenes.has(key)) return true;
      }
    }
    return false;
  }

  /**
   * The banner's lines, one each: that a request is in progress, first, while one is or a finished
   * one's scenes are still being added; why the latest press was refused, if the state can't say;
   * that archived scenes need a restore first (`restoreFirst`); and, as a hint, what a press does
   * now — not while scenes are still being added, which a press can't hurry. No counts (each
   * scene says its own, and the progress bar counts a request through), and a sensor that isn't
   * switched on yet goes unmentioned.
   */
  get lines(): StacBannerLine[] {
    const view = this.view;
    if (!view) return [];
    const lines: StacBannerLine[] = [];
    // A request running, or its scenes still being added after it: the same line either way.
    if (this.active || (view.request && this.adding)) lines.push({ text: this.ACTIVE_HINT, tone: 'hint' });
    if (this.notice) lines.push({ text: this.notice.message, tone: this.notice.type });
    if (this.restoreFirst) lines.push({ text: this.RESTORE_HINT, tone: 'warning' });
    // What a press does, only while there is a button to press (D6).
    if (!this.active && this.needsSync) lines.push({ text: this.ADD_HINT, tone: 'hint' });
    return lines;
  }

  /** The icon a line's tone shows before it, or null for none. */
  iconOf(line: StacBannerLine): string | null {
    return TONE_ICONS[line.tone];
  }

  trackLine(index: number, line: StacBannerLine): string {
    return line.tone + ':' + line.text;
  }

  /**
   * While a request is active: how many of the task's delivered scenes on switched-on sensors have
   * an outcome, out of how many there are. Null otherwise, and while every such sensor is archived.
   * An archived sensor's scenes aren't counted: a request skips them (`restoreFirst` says why).
   */
  get progress(): { done: number; total: number; percent: number } | null {
    const view = this.view;
    if (!view || !this.active) return null;
    const archived = this.archivedSensorIds;
    const counted = (sensorUuid: string) => view.sensors.get(sensorUuid) === 'on' && !archived.has(sensorUuid);
    let total = 0;
    for (const sensorUuid of view.sensors.keys()) if (counted(sensorUuid)) total += this.deliveredCounts.get(sensorUuid) || 0;
    if (!total) return null;
    let outcomes = 0;
    for (const entry of view.scenes.values()) {
      if (counted(entry.sensorUuid) && (entry.status === 'success' || entry.status === 'skipped' || entry.status === 'error')) outcomes += 1;
    }
    const done = Math.min(total, outcomes);
    return { done, total, percent: Math.round((done / total) * 100) };
  }

  async press(): Promise<void> {
    if (!this.view || this.active || this.pressing) return;
    this.pressing = true;
    this.notice = null;
    this.cdr.markForCheck();
    try {
      this.notice = (await this.stac.press(this.taskId)).notice;
    } finally {
      this.pressing = false;
      this.cdr.markForCheck();
    }
  }

  /** Listens to the task now shown, and reads its state. */
  private show(): void {
    this.stopPolling();
    this.viewSub?.unsubscribe();
    this.viewSub = undefined;
    this.view = undefined;
    this.notice = null;
    this.shownTask = this.taskId || null;
    if (this.eligible && this.taskId) {
      const taskId = this.taskId;
      this.viewSub = this.stac.view$(taskId).subscribe((view) => {
        this.view = view;
        if (view !== undefined) this.announce(this.visible);
        this.schedulePoll();
        this.cdr.markForCheck();
      });
      this.stac.refresh(taskId).catch(() => undefined);
    } else {
      this.announce(false);
    }
    this.cdr.markForCheck();
  }

  /** Tells the page whether anything shows, when that changes. */
  private announce(shown: boolean): void {
    if (shown === this.announced) return;
    this.announced = shown;
    this.shown.emit(shown);
  }

  /** While a request is queued or running, reads the state again in POLL_MS; one read at a time. */
  private schedulePoll(): void {
    if (this.poll || !this.active) return;
    const taskId = this.taskId;
    this.poll = setTimeout(() => {
      this.poll = undefined;
      // A failed read leaves the view as it was, and is tried again.
      this.stac.refresh(taskId).catch(() => this.schedulePoll());
    }, StacConversionComponent.POLL_MS);
  }

  private stopPolling(): void {
    if (this.poll) clearTimeout(this.poll);
    this.poll = undefined;
  }
}
